import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Button,
  Autocomplete,
  Chip,
  Box,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { apiService } from '../services/api';
import { Role, CreateRole, UpdateRole, Permission, RoleAuditLog, PagedResult } from '../types/api';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import AuditLogViewer from './AuditLogViewer';
import { appColors } from '../theme/colors';
import { listPageDialogPaperProps } from '../theme/darkDialog';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions as permissionCatalog } from '../constants/permissions';
import { useDeleteConfirmation } from '../hooks/useDeleteConfirmation';
import {
  ListPageRoot,
  ListPageToolbar,
  ListPageTableSurface,
  ListPageContentSection,
  ListPageMobileStack,
  ListPageTableHeadRow,
  ListPageTh,
  listPageTableBodyClass,
  listPageTableRowClass,
} from './listPage';

interface RolesTabProps {
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  onRegisterCreateAction?: (handler: (() => void) | null) => void;
}

const RolesTab: React.FC<RolesTabProps> = ({ onError, onSuccess, onRegisterCreateAction }) => {
  const { user } = useAuth();
  const { confirmDelete, deleteConfirmationDialog } = useDeleteConfirmation();
  const canCreateRole = hasPermission(user, permissionCatalog.userManagement.createRole);
  const canEditRole = hasPermission(user, permissionCatalog.userManagement.editRole);
  const canDeleteRole = hasPermission(user, permissionCatalog.userManagement.deleteRole);
  const [pagedResult, setPagedResult] = useState<PagedResult<Role> | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortDescending, setSortDescending] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0);
  const [roleAuditLogs, setRoleAuditLogs] = useState<RoleAuditLog[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  
  const [roleFormData, setRoleFormData] = useState<CreateRole>({
    roleName: '',
    description: '',
    permissionIds: [],
  });

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const filter = {
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: itemsPerPage,
        sortBy: sortBy,
        sortDescending: sortDescending,
      };
      const result = await apiService.getRoles(filter);
      setPagedResult(result);
    } catch (err: any) {
      onError('Failed to fetch roles. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onError, searchTerm, currentPage, itemsPerPage, sortBy, sortDescending]);

  const fetchPermissions = useCallback(async () => {
    try {
      setPermissionsLoading(true);
      const permissionsData = await apiService.getPermissions();
      setPermissions(permissionsData);
    } catch (err: any) {
      console.error('Failed to fetch permissions:', err);
      onError('Failed to load permissions. Please check your authentication and try again.');
    } finally {
      setPermissionsLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, [fetchRoles, fetchPermissions]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleOpenRoleDialog = (role?: Role) => {
    setActiveTab(0); // Reset to Details tab
    if (role) {
      setEditingRole(role);
      setRoleFormData({
        roleName: role.roleName,
        description: role.description || '',
        permissionIds: role.permissions.map(p => p.permissionId),
      });
    } else {
      setEditingRole(null);
      setRoleFormData({
        roleName: '',
        description: '',
        permissionIds: [],
      });
    }
    
    // Ensure permissions are loaded when opening the dialog
    if (permissions.length === 0 && !permissionsLoading) {
      fetchPermissions();
    }
    
    setOpenRoleDialog(true);
  };

  const handleCloseRoleDialog = () => {
    setOpenRoleDialog(false);
    setEditingRole(null);
    setActiveTab(0);
    setRoleAuditLogs([]); // Clear audit logs
    setRoleFormData({
      roleName: '',
      description: '',
      permissionIds: [],
    });
  };

  const handleRoleSubmit = async (event: React.MouseEvent) => {
    event.preventDefault();
    try {
      if (editingRole) {
        if (!canEditRole) {
          onError('You do not have permission to edit roles.');
          return;
        }
        const updateData: UpdateRole = {
          roleName: roleFormData.roleName,
          description: roleFormData.description,
          permissionIds: roleFormData.permissionIds,
        };
        await apiService.updateRole(editingRole.roleId, updateData);
        onSuccess('Role updated successfully!');
      } else {
        if (!canCreateRole) {
          onError('You do not have permission to create roles.');
          return;
        }
        await apiService.createRole(roleFormData);
        onSuccess('Role created successfully!');
      }

      handleCloseRoleDialog();
      fetchRoles();
    } catch (err: any) {
      onError(err.response?.data?.message || 'Operation failed. Please try again.');
    }
  };

  const handleDeleteRole = async (id: number) => {
    if (!canDeleteRole) {
      onError('You do not have permission to delete roles.');
      return;
    }
    const confirmed = await confirmDelete({ message: 'Are you sure you want to delete this role?' });
    if (!confirmed) return;
    try {
      await apiService.deleteRole(id);
      onSuccess('Role deleted successfully!');
      fetchRoles();
    } catch (err: any) {
      onError(err.response?.data?.message || 'Failed to delete role.');
    }
  };

  useEffect(() => {
    onRegisterCreateAction?.(canCreateRole ? () => handleOpenRoleDialog() : null);
    return () => onRegisterCreateAction?.(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- register header action only when parent setter identity changes
  }, [onRegisterCreateAction, canCreateRole]);

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    
    // Lazy load audit logs when switching to Change History tab
    if (newValue === 1 && editingRole && roleAuditLogs.length === 0) {
      setAuditLoading(true);
      try {
        const logs = await apiService.getRoleAuditLogs(editingRole.roleId);
        setRoleAuditLogs(logs);
      } catch (err: any) {
        onError('Failed to fetch audit logs');
      } finally {
        setAuditLoading(false);
      }
    }
  };

  const handlePermissionChange = (event: any, newValue: Permission[]) => {
    setRoleFormData(prev => ({
      ...prev,
      permissionIds: newValue.map(permission => permission.permissionId)
    }));
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return 'unfold_more';
    return sortDescending ? 'arrow_downward' : 'arrow_upward';
  };

  const currentRoles = pagedResult?.items || [];
  const totalPages = pagedResult?.totalPages || 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <CircularProgress />
      </div>
    );
  }

  return (
    <ListPageRoot>
      <ListPageToolbar className="flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <SearchBar
          variant="listPage"
          placeholder="Search roles..."
          value={searchTerm}
          onChange={setSearchTerm}
          className="w-full sm:max-w-md"
        />
      </ListPageToolbar>

      <ListPageContentSection>
        <ListPageTableSurface>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <ListPageTableHeadRow>
                  <ListPageTh scope="col" className="cursor-pointer" onClick={() => handleSort('id')}>
                    <div className="flex items-center gap-1">
                      ID
                      <span className="material-symbols-outlined text-base">{getSortIcon('id')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh scope="col" className="cursor-pointer" onClick={() => handleSort('rolename')}>
                    <div className="flex items-center gap-1">
                      Role Name
                      <span className="material-symbols-outlined text-base">{getSortIcon('rolename')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh scope="col" className="cursor-pointer" onClick={() => handleSort('description')}>
                    <div className="flex items-center gap-1">
                      Description
                      <span className="material-symbols-outlined text-base">{getSortIcon('description')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh scope="col">Permissions</ListPageTh>
                  <ListPageTh scope="col" className="text-right">
                    Actions
                  </ListPageTh>
                </ListPageTableHeadRow>
              </thead>
              <tbody className={listPageTableBodyClass}>
                {currentRoles.map((role) => (
                  <tr key={role.roleId} className={listPageTableRowClass}>
                    <td className="px-5 py-4 text-slate-300">#{role.roleId}</td>
                    <td className="px-5 py-4 text-white font-medium">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-base text-slate-400">security</span>
                        {role.roleName}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate-200">{role.description || 'No description'}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1">
                        {role.permissions.slice(0, 3).map((permission) => (
                          <span
                            key={permission.permissionId}
                            className="px-2 py-1 text-xs bg-primary/20 text-primary rounded-full"
                          >
                            {permission.permissionName}
                          </span>
                        ))}
                        {role.permissions.length > 3 && (
                          <span className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded-full">
                            +{role.permissions.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="action-row-icon-buttons">
                        {canEditRole && (
                          <button
                            type="button"
                            className="text-primary hover:bg-primary/10"
                            aria-label="Edit role"
                            onClick={() => handleOpenRoleDialog(role)}
                          >
                            <span className="material-symbols-outlined text-base">edit</span>
                          </button>
                        )}
                        {canDeleteRole && (
                          <button
                            type="button"
                            className="text-red-400 hover:bg-red-500/10"
                            aria-label="Delete role"
                            onClick={() => handleDeleteRole(role.roleId)}
                          >
                            <span className="material-symbols-outlined text-base">delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ListPageTableSurface>

        <ListPageMobileStack>
          {currentRoles.map((role) => (
            <div
              key={role.roleId}
              className="bg-elevation-dark rounded-xl p-4 border border-border-dark shadow-lg flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="material-symbols-outlined text-base text-primary shrink-0">security</span>
                    <h3 className="text-white font-semibold text-lg">{role.roleName}</h3>
                    <span className="text-xs text-slate-500">#{role.roleId}</span>
                  </div>
                  {role.description && <p className="text-slate-400 text-sm">{role.description}</p>}
                </div>
              </div>

              <div className="flex flex-wrap gap-1">
                {role.permissions.slice(0, 4).map((permission) => (
                  <span
                    key={permission.permissionId}
                    className="px-2 py-1 text-xs bg-primary/20 text-primary rounded-full"
                  >
                    {permission.permissionName}
                  </span>
                ))}
                {role.permissions.length > 4 && (
                  <span className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded-full">
                    +{role.permissions.length - 4} more
                  </span>
                )}
              </div>

              <div className="action-row-icon-buttons border-t border-border-dark/50 pt-3">
                {canEditRole && (
                  <button
                    type="button"
                    className="text-primary hover:bg-primary/10"
                    onClick={() => handleOpenRoleDialog(role)}
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                )}
                {canDeleteRole && (
                  <button
                    type="button"
                    className="text-red-400 hover:bg-red-500/10"
                    onClick={() => handleDeleteRole(role.roleId)}
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </ListPageMobileStack>
      </ListPageContentSection>

      {totalPages > 1 && (
        <div className="px-4 lg:px-5 pb-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
          />
        </div>
      )}

      {/* Role Dialog */}
      <Dialog 
        open={openRoleDialog} 
        onClose={handleCloseRoleDialog}
        maxWidth="md" 
        fullWidth
        PaperProps={listPageDialogPaperProps}
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
          {editingRole ? `Edit Role - ${editingRole.roleName}` : 'Create New Role'}
        </DialogTitle>
        
        {editingRole && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Details" />
              <Tab label="Change History" />
            </Tabs>
          </Box>
        )}
        
        <DialogContent sx={{ px: 3 }}>
          {activeTab === 0 ? (
          <div className="pt-1 space-y-4">
            <TextField
              autoFocus
              margin="dense"
              label="Role Name"
              fullWidth
              variant="outlined"
              value={roleFormData.roleName}
              onChange={(e) => setRoleFormData({ ...roleFormData, roleName: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={roleFormData.description}
              onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
            />
            <Autocomplete
              multiple
              options={permissions}
              getOptionLabel={(option) => option.permissionName}
              value={permissions.filter(p => roleFormData.permissionIds.includes(p.permissionId))}
              onChange={handlePermissionChange}
              loading={permissionsLoading}
              disabled={permissionsLoading}
              filterOptions={(options, { inputValue }) => {
                return options.filter(option =>
                  option.permissionName.toLowerCase().includes(inputValue.toLowerCase()) ||
                  (option.description && option.description.toLowerCase().includes(inputValue.toLowerCase()))
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search and select permissions"
                  placeholder={permissionsLoading ? "Loading permissions..." : "Type to search permissions..."}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {permissionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.permissionId}
                    label={option.permissionName}
                    size="small"
                    sx={{ 
                      backgroundColor: appColors.primary,
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'white',
                      },
                    }}
                  />
                ))
              }
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps} sx={{ backgroundColor: appColors.dialogBorder, color: 'white' }}>
                    <Box>
                      <Typography variant="body1" fontWeight="medium" sx={{ color: 'white' }}>
                        {option.permissionName}
                      </Typography>
                      {option.description && (
                        <Typography variant="body2" sx={{ color: appColors.mutedText }}>
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              noOptionsText={
                permissionsLoading 
                  ? "Loading permissions..." 
                  : permissions.length === 0 
                    ? "No permissions available. Please check your authentication." 
                    : "No permissions found"
              }
            />
          </div>
          ) : (
            <AuditLogViewer auditLogs={roleAuditLogs} loading={auditLoading} />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          {activeTab === 0 && (
            <>
              <Button 
                onClick={handleCloseRoleDialog}
                variant="outlined"
                sx={{ 
                  color: appColors.mutedText, 
                  borderColor: appColors.dialogBorder,
                  '&:hover': {
                    borderColor: appColors.primary,
                    color: appColors.primary,
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleRoleSubmit} 
                variant="contained"
                sx={{
                  backgroundColor: appColors.primary,
                  '&:hover': {
                    backgroundColor: appColors.primary,
                    opacity: 0.9,
                  }
                }}
              >
                {editingRole ? 'Update' : 'Create'}
              </Button>
            </>
          )}
          {activeTab === 1 && (
            <Button 
              onClick={handleCloseRoleDialog}
              variant="outlined"
              sx={{ 
                color: appColors.mutedText, 
                borderColor: appColors.dialogBorder,
                '&:hover': {
                  borderColor: appColors.primary,
                  color: appColors.primary,
                }
              }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {deleteConfirmationDialog}
    </ListPageRoot>
  );
};

export default RolesTab;